import type { QuoteVariant } from './domain'

/**
 * UID-440 (PHASE 0) — the single source of truth for the RFQ pricing-region
 * "group" vocabulary (title / sublabel / tooltip per North America vs
 * Overseas), extracted out of `QuoteVariantSelector.tsx`'s formerly-inline
 * `REGION_GROUPS` const so every other RFQ surface that needs to talk about
 * "which region group is this variant in" — today, the read-only
 * `RfqDetailSummaryRail` Region row; potentially other surfaces later — reads
 * the IDENTICAL wording instead of re-deriving its own copy that can silently
 * drift from the selector's (the exact bug class this ticket exists to
 * prevent: two surfaces both showing "region", one saying "North America",
 * the other saying something else, because each carried its own inline
 * const).
 *
 * `REGION_GROUPS` must be kept in sync with any future addition to
 * `QuoteVariant['id']` — an id that matches none of these groups resolves to
 * `undefined` from `groupTitleForVariantId` below. Callers own their own
 * fallback for that case: `QuoteVariantSelector` synthesizes a rendered
 * "Other options" fallback group of its own; `RfqDetailSummaryRail` falls
 * back to the raw server-authored `variant.region` string rather than
 * blanking the row.
 */
export interface RegionGroup {
  readonly key: string
  readonly title: string
  readonly variantIds: ReadonlyArray<QuoteVariant['id']>
  /** Fixed sub-label rendered under every option's lead-time line in this
   * group — a per-GROUP constant, not derived from `variant.region` (see
   * `QuoteVariantSelector.tsx`'s `.quote-variant-sublabel` doc comment for
   * the full rationale: the reference design shows one sub-label per
   * COLUMN, not one per option). */
  readonly sublabel: string
  /** Tooltip copy for the group's info (ⓘ) affordance in
   * `QuoteVariantSelector`. Not read by any other current consumer. */
  readonly tooltip: string
}

export const REGION_GROUPS: readonly RegionGroup[] = [
  {
    key: 'north-america',
    title: 'North America',
    variantIds: ['us', 'prefer_us_blend'],
    sublabel: 'USA or Mexico',
    tooltip: 'Parts sourced and produced in the USA or Mexico.',
  },
  {
    key: 'overseas',
    title: 'Overseas',
    variantIds: ['offshore', 'offshore_economy'],
    sublabel: 'Overseas production',
    tooltip:
      'Parts sourced and produced internationally — typically lower unit cost, longer lead time.',
  },
]

/**
 * Resolves a `QuoteVariant['id']` to its region GROUP TITLE ("North America" /
 * "Overseas") — `undefined` for an id that matches no group (an id landing
 * ahead of a `REGION_GROUPS` update, or a future non-regional variant).
 * Callers own their own fallback for the `undefined` case (see this module's
 * doc comment); this resolver never fabricates a title.
 */
export function groupTitleForVariantId(id: QuoteVariant['id']): string | undefined {
  return REGION_GROUPS.find((group) => group.variantIds.includes(id))?.title
}

/**
 * UID-325 — the design's per-unit qualifier (`dc.html:3528`), shared so every
 * RFQ surface that renders a per-unit price suffix uses the identical text
 * instead of a hand-typed literal that can drift. Carries a LEADING SPACE —
 * concatenate it directly onto the formatted amount string, or (as
 * `QuoteVariantSelector.tsx` does) render it inside its own child `<span>` of
 * the price element, so the space survives JSX's whitespace-collapsing and
 * the amount + unit still read as one run to a screen reader.
 */
export const QUOTE_PRICE_UNIT_SUFFIX = ' /ea'
